import { FiImage, FiTrash2 } from 'react-icons/fi';
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import MediaPickerModal from '../common/MediaPickerModal';
import SafeIcon from '../../common/SafeIcon';
import useSocialPlatforms from '../../hooks/useSocialPlatforms';
import { platformService } from '../../services/platformIntegrations';
import { useWorkspace } from '../../context/WorkspaceContext';
import { supabase } from '../../supabase';


const EditPostModal = ({ post, onSave, onCancel, onDelete }) => {
  const { activeWorkspace } = useWorkspace();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [versions, setVersions] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [platform, setPlatform] = useState([]); // Now an array for multi-select
  const [scheduledAt, setScheduledAt] = useState('');
  const [image, setImage] = useState(null);
  const [mediaType, setMediaType] = useState('image');
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const { platforms } = useSocialPlatforms();
  const [connectedPlatformIds, setConnectedPlatformIds] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [initiativeId, setInitiativeId] = useState('');
  const [isEvent, setIsEvent] = useState(false);


  useEffect(() => {
    const fetchConnections = async () => {
      if (!activeWorkspace) return;
      try {
        const connections = await platformService.getConnections(activeWorkspace.id);
        setConnectedPlatformIds(connections.map(c => c.platform_id));
      } catch (error) {
        console.error('Error fetching connections:', error);
      }
    };
    fetchConnections();

    const fetchInitiatives = async () => {
      if (!activeWorkspace) return;
      const { data } = await supabase.from('initiatives').select('id, title').eq('workspace_id', activeWorkspace.id);
      setInitiatives(data || []);
    };
    fetchInitiatives();
  }, [activeWorkspace]);

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setInitiativeId(post.initiative_id || '');
      setContent(post.content);

      const initialPlatforms = Array.isArray(post.platforms) && post.platforms.length > 0
          ? post.platforms
          : (post.platform ? [post.platform] : []);

      const legacyReverseMap = {
        'facebook': 'facebook_page',
        'twitter': 'twitter_social',
        'tiktok': 'tiktok_social',
        'linkedin': 'linkedin_page'
      };
      const normalizedInitial = initialPlatforms.map(p => legacyReverseMap[p] || p);
      const dedupedPlatforms = [...new Set(normalizedInitial)];

      let initialVersions = post.versions || [];

      // Ensure versions are seeded with the existing schedule to prevent accidental overwrites
      if (dedupedPlatforms.length > 0) {
          const fallbackSchedule = (post.scheduled_at && post.scheduled_at !== "Multiple Dates") ? post.scheduled_at : null;
          initialVersions = dedupedPlatforms.map(p => {
              const existingVersion = initialVersions.find(v => v.platform === p);
              if (existingVersion) {
                  return { ...existingVersion, scheduled_at: existingVersion.scheduled_at || fallbackSchedule };
              }
              return { platform: p, content: post.content, scheduled_at: fallbackSchedule };
          });
      }
      setVersions(initialVersions);
      setPlatform(dedupedPlatforms);
      if (dedupedPlatforms.length > 0) setActiveTab(dedupedPlatforms[0]);

      if (post.scheduled_at && post.scheduled_at !== "Multiple Dates") {
          setScheduledAt(format(new Date(post.scheduled_at), "yyyy-MM-dd'T'HH:mm"));
      }
      setImage(post.image);
      setMediaType(post.media_type || 'image');
      setIsEvent(post.is_event || false);
    }
  }, [post]);




  const getCharLimit = () => {
    // If we have an active tab (platform selected), use its limit. Otherwise use max of selected platforms.
    let currentPlatforms = [];
    if (activeTab) {
        currentPlatforms = [activeTab];
    } else {
        currentPlatforms = platform;
    }

    // Pick the most restrictive limit
    let limit = 5000;
    for (const p of currentPlatforms) {
        if (p === 'twitter' || p === 'twitter_social') {
            limit = Math.min(limit, 280);
        } else if (p.includes('linkedin')) {
            limit = Math.min(limit, 2500);
        } else if (p.includes('facebook')) {
            limit = Math.min(limit, 63206);
        } else if (p.includes('tiktok')) {
            limit = Math.min(limit, 2200);
        }
    }
    return limit;
  };

  const charLimit = getCharLimit();
  const remainingChars = charLimit - (content ? content.length : 0);
  const isOverLimit = remainingChars < 0;

  const checkCharLimit = () => {
      if (content && content.length > charLimit) {
          return window.confirm(`Your post is ${content.length} characters, but the limit is ${charLimit} for one or more selected platforms. It will be truncated. Do you want to continue?`);
      }
      return true;
  };

  const handleSave = () => {
    if (!checkCharLimit()) return;
    const scheduledDate = new Date(scheduledAt);
    const now = new Date();

    const isFuture = scheduledDate > now;
    const isValidDate = !isNaN(scheduledDate.getTime());

    onSave(post.id, {
      title,
      content,
      versions,
      platforms: platform,         // array of all selected platforms
      scheduled_at: isValidDate ? scheduledDate.toISOString() : null,
      image,
      media_type: mediaType,
      initiative_id: initiativeId || null,
      is_event: isEvent,
      ...(isFuture && { status: 'scheduled' })
    });
  };

  const handlePublishNow = () => {
    if (!checkCharLimit()) return;
    const now = new Date();
    onSave(post.id, {
      title,
      content,
      versions,
      platforms: platform,
      scheduled_at: now.toISOString(),
      status: 'published',
      initiative_id: initiativeId || null,
      is_event: isEvent
    });
  };

  const togglePlatform = (platformId, originalId) => {
    setPlatform(prev => {
      const exists = prev.includes(platformId) || prev.includes(originalId);
      const newPlatforms = exists
        ? prev.filter(id => id !== platformId && id !== originalId)
        : [...prev, platformId];

      if (!exists && newPlatforms.length === 1) {
          setActiveTab(platformId);
      } else if (exists && activeTab === platformId && newPlatforms.length > 0) {
          setActiveTab(newPlatforms[0]);
      } else if (exists && newPlatforms.length === 0) {
          setActiveTab(null);
      }

      if (!exists && newPlatforms.length > 1 && !activeTab) {
          setActiveTab(platformId);
      } else if (!exists && newPlatforms.length === 2 && prev.length === 1 && !activeTab) {
           setActiveTab(prev[0]);
      } else if (!exists && newPlatforms.length > 0 && !activeTab) {
           setActiveTab(newPlatforms[0]);
      }

      return newPlatforms;
    });
  };

  const handleContentChange = (val) => {
      setContent(val);
      if (activeTab) {
          setVersions(prev => {
              const existingIndex = prev.findIndex(v => v.platform === activeTab);
              if (existingIndex >= 0) {
                  const newVersions = [...prev];
                  newVersions[existingIndex] = { ...newVersions[existingIndex], content: val };
                  return newVersions;
              }
              return [...prev, { platform: activeTab, content: val }];
          });
      }
  };

  const handleScheduledAtChange = (val) => {
      setScheduledAt(val);
      if (activeTab) {
          setVersions(prev => {
              const existingIndex = prev.findIndex(v => v.platform === activeTab);
              const isValidDate = val && !isNaN(new Date(val).getTime());
              if (existingIndex >= 0) {
                  const newVersions = [...prev];
                  newVersions[existingIndex] = { ...newVersions[existingIndex], scheduled_at: isValidDate ? new Date(val).toISOString() : null };
                  return newVersions;
              }
              return [...prev, { platform: activeTab, content, scheduled_at: isValidDate ? new Date(val).toISOString() : null }];
          });
      }
  };

  useEffect(() => {
      if (activeTab) {
          const version = versions.find(v => v.platform === activeTab);
          if (version) {
              setContent(version.content || '');
              if (version.scheduled_at && version.scheduled_at !== "Multiple Dates") {
                  setScheduledAt(format(new Date(version.scheduled_at), "yyyy-MM-dd'T'HH:mm"));
              } else {
                  setScheduledAt(post?.scheduled_at && post.scheduled_at !== "Multiple Dates" ? format(new Date(post.scheduled_at), "yyyy-MM-dd'T'HH:mm") : '');
              }
          } else {
              setContent(post?.content || '');
              setScheduledAt(post?.scheduled_at && post.scheduled_at !== "Multiple Dates" ? format(new Date(post.scheduled_at), "yyyy-MM-dd'T'HH:mm") : '');
          }
      }
  }, [activeTab]);


  if (!post) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] md:max-h-[90dvh] flex flex-col overflow-hidden">
        <div className="p-6 border-b shrink-0">
            <h3 className="text-xl font-bold text-charcoal-900">Edit Post</h3>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4 min-h-0">
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Internal post title (e.g. Q3 Launch Announcement)"
              className="w-full p-3 border border-charcoal-200 rounded-lg text-charcoal-900 focus:ring-2 focus:ring-charcoal-900 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Link to Initiative (Optional)</label>
            <select
                value={initiativeId || ''}
                onChange={e => setInitiativeId(e.target.value)}
                className="w-full p-3 border border-charcoal-200 rounded-lg text-charcoal-900 focus:ring-2 focus:ring-charcoal-900 focus:border-transparent"
            >
                <option value="">None</option>
                {initiatives.map(init => (
                    <option key={init.id} value={init.id}>{init.title}</option>
                ))}
            </select>
          </div>


          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isEvent}
                onChange={(e) => setIsEvent(e.target.checked)}
                className="rounded border-charcoal-300 text-charcoal-900 focus:ring-charcoal-900"
              />
              <span className="text-sm font-medium text-charcoal-700">Mark as Event on Calendar</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Platforms</label>
            <div className="flex flex-wrap gap-2">
              {platforms.map(p => {
                const isConnected = connectedPlatformIds.includes(p.original_id);
                const isSelected = platform.includes(p.id) || platform.includes(p.original_id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlatform(p.id, p.original_id)}
                    disabled={!isConnected}
                    className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      !isConnected
                        ? 'bg-charcoal-50 border-charcoal-200 text-charcoal-400 opacity-60 cursor-not-allowed'
                        : isSelected
                          ? 'bg-charcoal-900 border-charcoal-900 text-white'
                          : 'bg-white border-charcoal-200 text-charcoal-700 hover:bg-charcoal-50'
                    }`}
                  >
                     <span>{p.name}</span>
                     {!isConnected && <span className="text-[10px] uppercase tracking-wider">(Not Connected)</span>}
                  </button>
                );
              })}
            </div>
            {platform.length === 0 && (
              <p className="text-red-500 text-xs mt-1">Please select at least one platform.</p>
            )}
          </div>

          {platform.length > 0 && (
          <div>
             <div className="flex space-x-2 border-b border-charcoal-200 mb-4 overflow-x-auto">
                 {platform.map(p => {
                     const pData = (platforms || []).find(sp => sp.id === p || sp.original_id === p);
                     return (
                         <button
                            key={p}
                            onClick={() => setActiveTab(p)}
                            className={`px-4 py-2 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === p ? 'border-charcoal-900 text-charcoal-900' : 'border-transparent text-charcoal-500 hover:text-charcoal-700'}`}
                         >
                             {pData ? pData.name : p} Copy
                         </button>
                     )
                 })}
             </div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Content</label>
            <textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              className="w-full h-40 p-3 border border-charcoal-200 rounded-lg text-charcoal-900 focus:ring-2 focus:ring-charcoal-900 focus:border-transparent"
            />
            <div className={`text-xs mt-1 flex flex-col gap-1 ${isOverLimit ? 'text-red-500 font-bold' : remainingChars < 100 ? 'text-orange-500 font-semibold' : 'text-charcoal-500'}`}>
              <div>
                <span className="text-sm">{content?.length || 0}</span> / {charLimit}
              </div>
              {isOverLimit && (
                <span className="warning block mt-1">
                  ⚠️ Post exceeds {activeTab || 'selected'} API limit by {Math.abs(remainingChars)} characters
                </span>
              )}
              {!isOverLimit && remainingChars < 100 && remainingChars >= 0 && (
                <span className="caution block mt-1">
                  ⚡ Approaching character limit
                </span>
              )}
            </div>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Scheduled Time {platform.length > 1 && <span className="text-xs text-charcoal-500 font-normal ml-2">(for {activeTab ? (platforms?.find(sp => sp.id === activeTab || sp.original_id === activeTab)?.name || activeTab) : 'selected platforms'})</span>}</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => handleScheduledAtChange(e.target.value)}
              className="w-full p-3 border border-charcoal-200 rounded-lg text-charcoal-900 focus:ring-2 focus:ring-charcoal-900 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Media</label>
            {image ? (
                <div className="relative inline-block group">
                    {(mediaType === 'video' || image.match(/\.(mp4|webm|mov)$/i)) ? (
                         <video
                            src={image}
                            className="h-32 w-auto rounded-lg border border-charcoal-200 object-cover"
                            controls
                        />
                    ) : (
                        <img
                            src={image}
                            alt="Post media"
                            className="h-32 w-auto rounded-lg border border-charcoal-200 object-cover"
                        />
                    )}
                    <button
                        onClick={() => { setImage(null); setMediaType('image'); }}
                        className="absolute -top-2 -right-2 bg-white text-red-500 rounded-full p-1 shadow-md hover:bg-red-50 border border-charcoal-100 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove media"
                    >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <button
                        onClick={() => setIsMediaPickerOpen(true)}
                        className="w-fit flex items-center gap-2 px-4 py-2 border border-charcoal-200 rounded-lg text-sm text-charcoal-600 hover:bg-charcoal-50 hover:border-charcoal-300 transition-colors"
                    >
                        <SafeIcon icon={FiImage} />
                        Add Image/Video from Library
                    </button>
                    <div className="flex items-center gap-2 max-w-md">
                        <span className="text-xs text-charcoal-500 uppercase font-medium">OR</span>
                    </div>
                    <div className="flex items-center gap-2 max-w-md">
                        <input
                            type="url"
                            placeholder="Paste image/video URL here..."
                            className="shrink-0 flex-1 p-2 border border-charcoal-200 rounded-lg bg-white text-charcoal-900 text-sm focus:ring-2 focus:ring-charcoal-900 focus:border-transparent"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = e.target.value;
                                    if (val) {
                                        const isVideo = /\.(mp4|mov|webm)$/i.test(val);
                                        setImage(val);
                                        setMediaType(isVideo ? 'video' : 'image');
                                        e.target.value = '';
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                const val = e.target.value;
                                if (val) {
                                    const isVideo = /\.(mp4|mov|webm)$/i.test(val);
                                    setImage(val);
                                    setMediaType(isVideo ? 'video' : 'image');
                                    e.target.value = '';
                                }
                            }}
                        />
                    </div>
                </div>
            )}
          </div>
        </div>
        <div className="shrink-0 p-6 border-t flex justify-end space-x-4 bg-charcoal-50 rounded-b-lg">
          {onDelete && (
            <button
                onClick={() => { if(window.confirm('Are you sure you want to delete this post?')) onDelete(post); }}
                className="px-4 py-2 rounded-md border text-red-600 border-red-200 hover:bg-red-50 mr-auto flex items-center gap-2"
            >
                <SafeIcon icon={FiTrash2} /> Delete
            </button>
          )}
          <button onClick={onCancel} className="px-4 py-2 rounded-md border text-charcoal-700 hover:bg-charcoal-50">Cancel</button>
          <button onClick={handlePublishNow} className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700">Publish Now</button>
          <button onClick={handleSave} className="px-4 py-2 rounded-md bg-charcoal-900 text-white hover:bg-charcoal-800">Save Changes</button>
        </div>
      </div>

      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={(asset) => {
            setImage(asset.image_url);
            setMediaType(asset.type);
        }}
      />
    </div>
  );
};

export default EditPostModal;
